import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Camera, ArrowLeft, User, Clock, Tag, Send } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';

export default function StoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [story, setStory] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStory();
    loadComments();
  }, [id]);

  const loadStory = async () => {
    try {
      const res = await axios.get(`${API}/stories/${id}`);
      setStory(res.data);
    } catch (error) {
      toast.error('Story not found');
      navigate('/stories');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const res = await axios.get(`${API}/comments/${id}`);
      setComments(res.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      await axios.post(`${API}/comments/${id}`, { content: commentText });
      setCommentText('');
      loadComments();
      toast.success('Comment added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/stories')}
                data-testid="back-to-stories-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Stories
              </Button>
              <div className="flex items-center space-x-2">
                <Camera className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-slate-800">FlockTracker</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Story Card */}
          <Card className="p-8 mb-8" data-testid="story-detail">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                {story.category}
              </span>
              {story.tags.map(tag => (
                <span key={tag} className="flex items-center gap-1 text-sm text-slate-500">
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">{story.title}</h1>

            <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {story.author_email}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {new Date(story.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {story.image_url && (
              <img 
                src={story.image_url} 
                alt={story.title} 
                className="w-full h-96 object-cover rounded-lg mb-6"
              />
            )}

            <div className="prose max-w-none">
              <p className="text-slate-700 text-lg leading-relaxed whitespace-pre-wrap">{story.content}</p>
            </div>
          </Card>

          {/* Comments Section */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">
              Comments ({comments.length})
            </h2>

            {user && (
              <form onSubmit={handleAddComment} className="mb-8" data-testid="comment-form">
                <Textarea
                  placeholder="Share your thoughts..."
                  rows={3}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="mb-3"
                  data-testid="comment-input"
                />
                <Button type="submit" data-testid="submit-comment-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Post Comment
                </Button>
              </form>
            )}

            {!user && (
              <div className="bg-slate-100 rounded-lg p-4 mb-8 text-center">
                <p className="text-slate-600">Please login to leave a comment</p>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4" data-testid="comments-list">
              {comments.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No comments yet. Be the first to comment!</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="border-b border-slate-200 pb-4 last:border-0" data-testid={`comment-${comment.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-slate-800">{comment.author_email}</span>
                      <span className="text-sm text-slate-500">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-slate-700">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}